// src/hooks/useTypesOperation.ts
//
// Hook React pour charger la liste des types d'opération comptable depuis
// la BD (table sc_metier.ecom_types_operation), via le service
// typesOperationService. Cache mémoire process-wide partagé avec le service.
//
// Usage :
//   const { types, loading, refresh } = useTypesOperation();
//   <select>
//     {types.filter(t => t.actif).map(t => (
//       <option key={t.code} value={t.code}>{t.libelle}</option>
//     ))}
//   </select>
//

import { useEffect, useState, useCallback } from 'react';
import {
  typesOperationService, TypeOperationDef, invalidateTypesOperationCache,
} from '../services/typesOperationService';

interface UseTypesOperationResult {
  /** Tous les types (actifs + inactifs) */
  types: TypeOperationDef[];
  /** Uniquement les types actifs (sélecteurs métier) */
  typesActifs: TypeOperationDef[];
  loading: boolean;
  /** Force un rechargement depuis l'API */
  refresh: () => Promise<void>;
  /** Helper : retourne le libellé d'un code (fallback = code brut). */
  labelOf: (code: string | undefined | null) => string;
  /** Filtrer les types par module (vide → tous les types). */
  byModule: (moduleCode?: string) => TypeOperationDef[];
}

export function useTypesOperation(): UseTypesOperationResult {
  const [types, setTypes] = useState<TypeOperationDef[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await typesOperationService.getAllAsync();
      setTypes(list);
    } catch {
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    invalidateTypesOperationCache();
    await load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await typesOperationService.getAllAsync();
        if (!cancelled) {
          setTypes(list);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setTypes([]);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const typesActifs = types.filter(t => t.actif);

  const labelOf = useCallback(
    (code: string | undefined | null) =>
      types.find(t => t.code === code)?.libelle || code || '—',
    [types],
  );

  const byModule = useCallback(
    (moduleCode?: string) => {
      if (!moduleCode) return typesActifs;
      return typesActifs.filter(t =>
        !t.moduleCodes || t.moduleCodes.length === 0 || t.moduleCodes.includes(moduleCode),
      );
    },
    [typesActifs],
  );

  return { types, typesActifs, loading, refresh, labelOf, byModule };
}
